import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOrgController, useCurrentOrgUser } from "../../auth/org-provider";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import Spinner from "../ui/spinner";
import { useUpdateOrgUserMutation } from "../../data/mutations/update-org-user";
import { BaseTone, baseToneSchema, assistantTonePreferencesSchema } from "tekkr-common/dist/model/user/tone";

const toneOptions: { value: BaseTone; label: string; description: string }[] = [
    { value: "default", label: "Default", description: "Default Tekkr assistant tone" },
    { value: "professional", label: "Professional", description: "Polished and precise" },
    { value: "casual", label: "Casual", description: "Friendly and conversational" },
    { value: "concise", label: "Concise", description: "Direct and to-the-point" },
];

const formSchema = z.object({
    baseTone: baseToneSchema,
    customInstructions: assistantTonePreferencesSchema.shape.customInstructions,
});

type FormType = z.infer<typeof formSchema>;

function ToneSettingsDialogContent(props: { onClose: () => void }) {
    const orgController = useOrgController();
    const currentUser = useCurrentOrgUser();

    const updateUserMutation = useUpdateOrgUserMutation({
        onSuccess: async () => {
            await orgController.refetch();
            props.onClose();
        },
    });

    const form = useForm<FormType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            baseTone: currentUser.assistantTonePreferences?.baseTone ?? "default",
            customInstructions: currentUser.assistantTonePreferences?.customInstructions ?? "",
        },
    });

    function onSubmit(values: FormType) {
        updateUserMutation.mutate({
            userId: currentUser.id,
            assistantTonePreferences: {
                baseTone: values.baseTone,
                customInstructions: values.customInstructions || undefined,
            },
        });
    }

    return (
        <DialogContent className="flex max-h-[720px] flex-col">
            <DialogHeader>
                <DialogTitle className="text-start">AI Tone Settings</DialogTitle>
                <DialogDescription className="text-start">
                    Customize how the Tekkr AI assistant communicates with you.
                </DialogDescription>
            </DialogHeader>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-8">
                    <FormField
                        control={form.control}
                        name="baseTone"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Communication Style</FormLabel>
                                <div className="pb-2 text-[0.8rem] text-muted-foreground">
                                    Set the style and tone of how Tekkr responds to you.
                                </div>
                                <FormControl>
                                    <Select
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        value={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a tone">
                                                    {field.value
                                                        ? toneOptions.find((t) => t.value === field.value)?.label
                                                        : "Select a tone"}
                                                </SelectValue>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {toneOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    <div className="flex flex-col items-start gap-0.5 py-0.5 text-start">
                                                        <span className="font-medium">{option.label}</span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {option.description}
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="customInstructions"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Custom Instructions</FormLabel>
                                <div className="pb-2 text-[0.8rem] text-muted-foreground">
                                    Add your own specific instructions to make the Tekkr AI assistant more effective.
                                </div>
                                <FormControl>
                                    <Textarea
                                        placeholder="e.g., Be concise and action-oriented..."
                                        className={`min-h-[150px] resize-none ${(field.value?.length ?? 0) > 3000 ? "border-destructive" : ""}`}
                                        disabled={updateUserMutation.isPending}
                                        {...field}
                                    />
                                </FormControl>
                                {(field.value?.length ?? 0) > 3000 && (
                                    <div className="pt-3 text-right text-xs font-medium text-muted-foreground animate-in fade-in slide-in-from-top-1">
                                        Used <span className="text-destructive">{field.value?.length ?? 0}</span> /{" "}
                                        <span className="text-destructive">3000</span> characters
                                    </div>
                                )}
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className="mt-auto flex flex-row justify-end pt-2">
                        <Button
                            type="submit"
                            disabled={
                                updateUserMutation.isPending ||
                                !form.formState.isDirty ||
                                (form.watch("customInstructions")?.length ?? 0) > 3000
                            }
                        >
                            {updateUserMutation.isPending ? <Spinner /> : "Save"}
                        </Button>
                    </div>
                </form>
            </Form>

            {updateUserMutation.isError && (
                <div className="rounded-md border border-destructive px-4 py-2 text-sm text-destructive">
                    Failed to save tone settings. Please try again.
                </div>
            )}
        </DialogContent>
    );
}

export default function ToneSettingsDialog(props: React.PropsWithChildren) {
    const [open, setOpen] = React.useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{props.children}</DialogTrigger>
            {open && <ToneSettingsDialogContent onClose={() => setOpen(false)} />}
        </Dialog>
    );
}
